// src/views/DesafiosListPage.js

import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, ChevronRight, Trash2 } from "lucide-react";
import * as api from "../api/ligaApi";
import { useNotification } from "../context/NotificationContext";
import LoadingAnimation from "../components/LoadingAnimation";

const DesafiosListPage = () => {
  const [desafios, setDesafios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newDesafioName, setNewDesafioName] = useState("");
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const carregarDesafios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicDesafios();
      setDesafios(data);
    } catch (error) {
      console.error("Erro ao carregar desafios", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) setIsAdmin(true);
    carregarDesafios();
  }, [carregarDesafios]);

  const handleCreateDesafio = async (e) => {
    e.preventDefault();
    if (!newDesafioName.trim()) return;
    try {
      const novoDesafio = await api.createDesafio({ nome: newDesafioName });
      setNewDesafioName("");
      setShowCreate(false);
      // Navega diretamente para o admin do novo desafio após a criação
      if (novoDesafio && novoDesafio.id) {
        navigate(`/desafio/${novoDesafio.id}/admin`);
      } else {
        carregarDesafios(); // Recarrega a lista como fallback
      }
    } catch (error) {
      alert("Erro ao criar desafio: " + error.message);
    }
  };

  // 2. Criar a função para gerir a exclusão
  const handleDeleteDesafio = async (desafioId, desafioNome, e) => {
    e.preventDefault(); // Impede a navegação do Link
    e.stopPropagation(); // Impede outros eventos de clique

    showNotification({
      title: "Confirmar Exclusão",
      message: `Tem a certeza ABSOLUTA que quer apagar o desafio "${desafioNome}"?\n\nTODOS os dados (duplas, times, pontuações) serão perdidos para sempre.`,
      type: "confirm",
      onConfirm: async () => {
        try {
          await api.deleteDesafio(desafioId);
          // 4. Substituir o alert de sucesso
          showNotification({
            title: "Sucesso",
            message: `Desafio "${desafioNome}" apagado com sucesso.`,
          });
          carregarDesafios();
        } catch (error) {
          // 5. Substituir o alert de erro
          showNotification({
            title: "Erro",
            message: "Erro ao apagar o desafio: " + error.message,
          });
        }
      },
    });
  };

  if (loading) return <LoadingAnimation size={200} />;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-teal-400">
          Desafios Disponíveis
        </h1>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"
          >
            <PlusCircle size={20} className="mr-2" />
            Novo Desafio
          </button>
        )}
      </div>

      {isAdmin && showCreate && (
        <form
          onSubmit={handleCreateDesafio}
          className="bg-gray-800 p-4 rounded-lg mb-6 flex gap-4"
        >
          <input
            type="text"
            value={newDesafioName}
            onChange={(e) => setNewDesafioName(e.target.value)}
            placeholder="Nome do novo desafio (ex: Brasileirão 2024)"
            className="flex-grow bg-gray-700 text-white p-2 rounded-md"
            required
          />
          <button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg"
          >
            Criar
          </button>
        </form>
      )}

      <div className="space-y-4">
        {desafios.length > 0 ? (
          desafios.map((desafio) => (
            <Link
              key={desafio.id}
              to={`/desafio/${desafio.id}`}
              className="block"
            >
              <div className="bg-gray-800 hover:bg-gray-700 p-5 rounded-lg cursor-pointer transition-all shadow-lg flex justify-between items-center">
                <div className="flex-grow">
                  <h2 className="text-xl font-bold text-white mb-1">
                    {desafio.nome}
                  </h2>
                  <p className="text-sm text-gray-400">
                    Status: {desafio.status}
                  </p>
                </div>
                <div className="flex items-center">
                  {/* 3. Adicionar o botão de apagar apenas para o admin */}
                  {isAdmin && (
                    <button
                      onClick={(e) =>
                        handleDeleteDesafio(desafio.id, desafio.nome, e)
                      }
                      className="text-red-500 hover:text-red-400 p-2 rounded-full hover:bg-gray-900/50 mr-2"
                      title="Apagar Desafio"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                  <ChevronRight className="text-gray-500" />
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="text-center bg-gray-800 p-8 rounded-lg">
            <p className="text-gray-400">
              Nenhum desafio disponível no momento.
            </p>
            {isAdmin && (
              <p className="text-gray-500 mt-2">
                Clique em "Novo Desafio" para começar.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesafiosListPage;
